import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { useAuth } from '../AuthContext';
import ReactDOM from 'react-dom';

// Portal component to render dropdowns to the document body
const DropdownPortal = ({ children, referenceRect }) => {
  const portalNode = document.getElementById('dropdown-portal') || (() => {
    const node = document.createElement('div');
    node.id = 'dropdown-portal';
    document.body.appendChild(node);
    return node;
  })();

  const style = referenceRect ? {
    position: 'absolute',
    top: `${referenceRect.bottom}px`,
    left: `${referenceRect.left}px`,
    zIndex: 99999,
    minWidth: `${referenceRect.width}px`,
  } : {};

  return ReactDOM.createPortal(
    <div style={style}>{children}</div>,
    portalNode
  );
};

const Navbar = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);

    // Add scroll listener to create a dynamic navbar effect
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };
        
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleAuthAction = () => {
        if (currentUser) {
            signOut(auth).then(() => {
                navigate('/');
            });
        } else {
            navigate('/login');
        }
    };

    // NavItem component for dropdown functionality
    const NavItem = ({ label, path, children }) => {
        const [isOpen, setIsOpen] = useState(false);
        const hasChildren = React.Children.count(children) > 0;
        const [itemRef, setItemRef] = useState(null);
        const [itemRect, setItemRect] = useState(null);
        
        // Update reference rect when element ref changes or when dropdown opens
        useEffect(() => {
            if (itemRef && isOpen) {
                const rect = itemRef.getBoundingClientRect();
                setItemRect({
                    top: rect.top + window.scrollY,
                    right: rect.right + window.scrollX,
                    bottom: rect.bottom + window.scrollY,
                    left: rect.left + window.scrollX,
                    width: rect.width,
                    height: rect.height
                });
            }
        }, [itemRef, isOpen]);
        
        return (
            <li 
                className="relative"
                onMouseEnter={() => setIsOpen(true)}
                onMouseLeave={() => setIsOpen(false)}
                ref={setItemRef}
            >
                <Link 
                    to={path} 
                    className={`font-bold transition-all duration-200 hover:text-white py-2 flex items-center ${
                        location.pathname === path ? 'text-white' : 'text-green-50'
                    }`}
                >
                    {label}
                    {hasChildren && (
                        <svg 
                            className={`ml-1 w-4 h-4 transition-transform duration-200 ${
                                isOpen ? 'rotate-180' : ''
                            }`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24" 
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth="2" 
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    )}
                </Link>
                
                {hasChildren && isOpen && itemRect && (
                    <DropdownPortal referenceRect={itemRect}>
                        <div className="mt-1 bg-white/20 backdrop-blur-xl py-2 rounded-lg shadow-lg min-w-max border border-white/30">
                            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 rounded-lg opacity-80"></div>
                            <div className="relative z-10">
                                {children}
                            </div>
                        </div>
                    </DropdownPortal>
                )}
            </li>
        );
    };

    // DropdownItem component for dropdown menu items
    const DropdownItem = ({ label, path }) => (
        <Link 
            to={path}
            className="block px-5 py-2 text-white font-bold hover:bg-white/20 whitespace-nowrap transition-colors duration-200"
        >
            {label}
        </Link>
    );

    return (
        <nav className={`sticky top-0 z-50 backdrop-blur-lg transition-all duration-300 ${scrolled ? 'bg-white/70 shadow-md' : 'bg-white/60'}`}>
            <div className="relative">
                {/* Gradient background with Apple Music style blur */}
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 opacity-80"></div>
                <div className="absolute inset-0 backdrop-blur-xl"></div>

                <div className="container mx-auto relative z-10">
                    <div className="flex justify-between items-center py-4 px-6">
                        <ul className="flex space-x-8 list-none p-0 m-0">
                            {/* Homepage */}
                            <NavItem label="Homepage" path="/" />

                            {/* Company Dropdown */}
                            <NavItem label="Company" path="/about">
                                <DropdownItem label="About Us" path="/about" />
                                <DropdownItem label="Contact Us" path="/company/contact" />
                            </NavItem>
                            
                            {/* Resources Dropdown */}
                            <NavItem label="Resources" path="/resources/carbon-farming">
                                <DropdownItem label="Understanding Carbon Farming" path="/resources/carbon-farming" />
                                <DropdownItem label="Research & Scientific Articles" path="/resources/research" />
                                <DropdownItem label="FAQs" path="/resources/faqs" />
                                <DropdownItem label="Customer Stories" path="/resources/customer-stories" />
                            </NavItem>
                            
                            {/* Carbon Calculator */}
                            <NavItem label="Carbon Calculator" path="/calculator" />
                            
                            {/* Carbon Data Ledger */}
                            <NavItem label="Carbon Data Ledger" path="/ledger" />
                        </ul>

                        <button 
                            onClick={handleAuthAction}
                            className="flex items-center justify-center bg-white/20 backdrop-blur-md hover:bg-white/30 text-white font-bold rounded-full px-6 py-2 transition-all duration-300 hover:shadow-lg"
                        >
                            <span>{currentUser ? 'Sign out' : 'Login'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;